import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Plus, type LucideIcon } from 'lucide-react';
import { Button } from '../../ui/Button';

/**
 * Grouped "insert" popover used by the builder's add-page and add-block
 * menus: a single trigger opens a panel of labelled, described options
 * organised into sections — easier to scan than a flat row of icons.
 */

export interface InsertItem {
  key: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  onSelect: () => void;
}

export interface InsertGroup {
  title: string;
  items: InsertItem[];
}

export function InsertMenu({
  triggerLabel,
  groups,
  align = 'left',
}: {
  triggerLabel: string;
  groups: InsertGroup[];
  /** Which edge the panel aligns to. */
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{ dropUp: boolean; maxH: number }>({
    dropUp: false,
    maxH: 320,
  });

  // Keep the panel on screen: open upward when there is more room above than
  // below the trigger, and cap the height to the space available so a long
  // menu near the bottom of the page scrolls internally instead of overflowing.
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const gap = 12;
    const below = window.innerHeight - rect.bottom - gap;
    const above = rect.top - gap;
    const dropUp = below < 260 && above > below;
    setPlacement({ dropUp, maxH: Math.max(180, Math.min(320, dropUp ? above : below)) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <Button
        variant="secondary"
        size="sm"
        iconLeft={<Plus className="h-4 w-4" />}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        {triggerLabel}
      </Button>
      {open ? (
        <div
          role="menu"
          style={{ maxHeight: placement.maxH }}
          className={`absolute z-20 w-72 overflow-y-auto rounded-token border border-line/10 bg-surface p-2 shadow-token-lg ${
            placement.dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          } ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {groups.map((group) => (
            <div key={group.title} className="mb-2 last:mb-0">
              <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-fg-dim">
                {group.title}
              </div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      item.onSelect();
                      setOpen(false);
                    }}
                    className="flex w-full items-start gap-2 rounded-token-sm px-2 py-1.5 text-left hover:bg-surface-2"
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" />
                    <span className="min-w-0">
                      <span className="block text-sm text-fg">{item.label}</span>
                      {item.description ? (
                        <span className="block text-xs text-fg-muted">{item.description}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
